import React from "react";
import { Box, Tabs, Tab, Switch, FormControlLabel } from "@mui/material";
import CustomTextField from "./CustomTextField";
import CustomDateField from "./CustomDateField";
import CustomSelect from "./CustomSelect";
import CustomButton from "./CustomButton";
import CustomDialog from "./CustomDialog";
import CustomLabel from "./CustomLabel";
import { COLORS } from "../assets/theme";

export interface Field {
  name: string;
  label: string;
  type?: "text" | "dropdown" | "file" | "password" | "date" | "number" | "switch";
  options?: string[] | { label: string; value: string }[];
  customRender?: (
    value: any,
    onChange: (value: any) => void,
    helpers?: {
      setFieldValue: (name: string, value: any) => void;
      values: Record<string, any>;
    },
    error?: string
  ) => React.ReactNode;
  required?: boolean;
  colSpan?: 1 | 2;
  disabled?: boolean;
  searchable?: boolean;
  /** Search text typed in a dropdown, e.g. to fetch more options from the server. */
  onSearchInput?: (term: string) => void;
  hidden?: boolean;
  showPassword?: boolean;
}

export interface GenericFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (values: Record<string, any>) => void;
  onTabChange?: (tabIndex: number) => void;
  fields?: Field[];
  tabs?: { label: string; fields: Field[] }[];
  taps?: number;
  title?: React.ReactNode;
  submitLabel?: string;
  initialValues?: Record<string, any>;
  disabled?: boolean;
  /**
   * When true, the submit button stays disabled until a field value changes
   * from the provided initialValues.
   */
  disableSubmitWhenUnchanged?: boolean;
  /**
   * When true, the submit button stays disabled while any required field in the
   * active view is empty.
   */
  disableSubmitUntilValid?: boolean;
  /**
   * The tab index to open on first render when tabs are enabled.
   */
  initialTab?: number;
  fieldColSpans?: Record<string, 1 | 2>;
  additionalActions?: React.ReactNode[];
  customContent?: React.ReactNode;
  showSubmit?: boolean;
  hidden?: boolean;
  onValuesChange?: (values: Record<string, any>) => void;
  showCloseIcon?: boolean;
  isSubmitting?: boolean;
}

const normalizeDateString = (val: string) => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  if (val.includes("T")) return val.split("T")[0];
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Accepts the partial values a user types on the way to a number ("", "-",
// "0.", ".5") so a controlled number field never eats the keystroke.
const isDecimalInput = (val: string) => /^-?\d*\.?\d*$/.test(val);

const GenericFormDialog: React.FC<GenericFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  fields = [],
  tabs,
  taps = 0,
  disabled,
  hidden,
  disableSubmitWhenUnchanged = false,
  disableSubmitUntilValid = false,
  initialTab,
  title,
  submitLabel = "Submit",
  initialValues,
  fieldColSpans,
  additionalActions = [],
  customContent,
  showSubmit = true,
  onTabChange,
  onValuesChange,
  showCloseIcon = true,
  isSubmitting = false,
}) => {
  const [values, setValues] = React.useState<Record<string, any>>({});
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = React.useState(0);
  const [initialSnapshot, setInitialSnapshot] =
    React.useState<Record<string, any>>({});

  const allFields = React.useMemo(() => {
    if (tabs && tabs.length) return tabs.flatMap((t) => t.fields || []);
    return fields;
  }, [fields, tabs]);

  const dateFieldNames = React.useMemo(
    () =>
      new Set(
        allFields
          .filter((f) => f.type === "date")
          .map((f) => f.name)
          .filter(Boolean)
      ),
    [allFields]
  );

  const customRenderFieldNames = React.useMemo(
    () =>
      new Set(
        allFields
          .filter((f) => typeof f.customRender === "function")
          .map((f) => f.name)
          .filter(Boolean)
      ),
    [allFields]
  );

  const getFieldsForActiveView = React.useCallback(() => {
    if (tabs && tabs.length) return tabs[activeTab]?.fields || [];
    if (taps && taps > 0) {
      const per = Math.ceil(fields.length / taps);
      const start = activeTab * per;
      return fields.slice(start, start + per);
    }
    return fields;
  }, [activeTab, fields, tabs, taps]);

  const activeFields = React.useMemo(
    () => getFieldsForActiveView(),
    [getFieldsForActiveView]
  );

  const normalizeValue = (val: any, key?: string) => {
    if (key && customRenderFieldNames.has(key)) {
      // Preserve complex data (arrays/objects) for custom renders (e.g. invoice items)
      return val === undefined || val === null ? "" : val;
    }
    const base = val === undefined || val === null ? "" : String(val);
    if (key && dateFieldNames.has(key)) {
      return normalizeDateString(base);
    }
    return base;
  };

  // Callers pass initialValues as an inline object, so its identity changes on
  // every parent render. Keying the reset on identity wiped whatever the user
  // had typed each time the parent re-rendered — compare the content instead.
  const initialValuesSignature = React.useMemo(() => {
    try {
      return JSON.stringify(initialValues ?? {});
    } catch {
      return "";
    }
  }, [initialValues]);

  React.useEffect(() => {
    if (open) {
      const resolvedInitialTab = typeof initialTab === "number" ? initialTab : 0;
      const normalizedInitial = Object.keys(initialValues || {}).reduce(
        (acc: Record<string, any>, key) => {
          acc[key] = normalizeValue((initialValues || {})[key], key);
          return acc;
        },
        {}
      );
      setValues(normalizedInitial);
      setInitialSnapshot(normalizedInitial);
      setErrors({});
      setActiveTab(resolvedInitialTab);
      onTabChange?.(resolvedInitialTab);
    } else {
      setValues({});
      setErrors({});
    }
    // initialValues and onTabChange are deliberately not dependencies: both
    // change identity every render and would re-seed the form mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValuesSignature, initialTab]);

  const handleChange = (field: string, value: any) => {
  setValues((prev) => {
    const updated = { ...prev, [field]: value };
    onValuesChange?.(updated);
    return updated;
  });
  };

  const isValueEmpty = (val: any) => {
    if (val === undefined || val === null) return true;
    if (typeof val === "string") return val.trim() === "";
    if (Array.isArray(val)) return val.length === 0;
    return false;
  };

  const isDirty = React.useMemo(() => {
    const keys = new Set([
      ...Object.keys(initialSnapshot),
      ...Object.keys(values),
    ]);
    let changed = false;
    keys.forEach((key) => {
      if (
        normalizeValue(values[key], key) !==
        normalizeValue(initialSnapshot[key], key)
      ) {
        changed = true;
      }
    });
    return changed;
  }, [initialSnapshot, values]);

  const hasMissingRequired = React.useMemo(() => {
    if (!disableSubmitUntilValid) return false;
    return activeFields.some(
      (f) => f.required && isValueEmpty(values[f.name])
    );
  }, [activeFields, disableSubmitUntilValid, values]);

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    const fieldsToValidate = activeFields;

    fieldsToValidate.forEach((f) => {
      if (f.required) {
        const val = values[f.name];
        if (isValueEmpty(val)) newErrors[f.name] = `${f.label || f.name} is required`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // passed validation
    if (onSubmit) {
      onSubmit(values);
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      showCloseIcon={showCloseIcon}
      paperSx={{ minWidth: { sm: 700 }, maxWidth: 1000 }}
      title={
        (tabs && tabs.length) || (taps && taps > 0) ? (
          <Box>
            {title && <Box sx={{ mb: 1 }}>{title}</Box>}
            <Tabs
              value={activeTab}
              onChange={(_, newIndex) => {
                setActiveTab(newIndex);
                onTabChange?.(newIndex);
              }}
              variant="fullWidth"
            >
              {tabs && tabs.length
                ? tabs.map((t, i) => (
                    <Tab key={t.label || i} label={t.label || `Tab ${i + 1}`} />
                  ))
                : Array.from({ length: taps }).map((_, i) => (
                    <Tab key={i} label={`Tab ${i + 1}`} />
                  ))}
            </Tabs>
          </Box>
        ) : (
          title
        )
      }
      actions={[
        ...additionalActions,
        <CustomButton
          key="cancel"
          label="Cancel"
          variant="outlined"
          onClick={onClose}
          sx={{ mr: 2 }}
        />,
        ...(showSubmit
          ? [
              <CustomButton
                key="submit"
                label={submitLabel}
                variant="contained"
                disabled={
                  Boolean(disabled) ||
                  (disableSubmitWhenUnchanged && !isDirty) ||
                  hasMissingRequired ||
                  isSubmitting
                }
                loading={isSubmitting}
                onClick={handleSubmit}
              />,
            ]
          : []),
      ]}
    >
      {customContent ? (
        customContent
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          {(() => {
            return activeFields.map((field) => {
              const colSpan = fieldColSpans
                ? fieldColSpans[field.name] ?? field.colSpan ?? 1
                : field.colSpan ?? 1;
              const spanFull = colSpan === 2;
              const cellProps = {
                key: field.name,
                sx: { gridColumn: spanFull ? "1 / -1" : undefined },
              };

              if (field.customRender) {
                const rendered = field.customRender(
                  values[field.name] ?? "",
                  (v: any) => handleChange(field.name, v),
                  {
                    setFieldValue: (name: string, v: any) => handleChange(name, v),
                    values,
                  },
                  errors[field.name]
                );
                if (rendered === null || rendered === undefined) {
                  return null;
                }
                return <Box {...cellProps}>{rendered}</Box>;
              }

              if (field.type === "date") {
                return (
                  <Box {...cellProps}>
                    <CustomDateField
                      label={field.label}
                      value={values[field.name] ?? ""}
                      onChange={(v: string) => handleChange(field.name, v)}
                      disabled={Boolean(disabled) || Boolean(field.disabled)}
                      fullWidth
                    />
                  </Box>
                );
              }

              if (field.type === "dropdown" && field.options) {
                const options = field.options.map((opt) =>
                  typeof opt === "string" ? { label: opt, value: opt } : opt
                );
                return (
                  <Box {...cellProps}>
                    {field.label && (
                      <CustomLabel
                        sx={{ p: 0.1 }}
                        text={field.label}
                        hidden={field.hidden}
                        highlightLabelStar={field.required || false}
                        color={COLORS.typographyColor}
                        fontWeight={500}
                        fontSize={14}
                      />
                    )}
                    <CustomSelect
                      label={field.label}
                      value={values[field.name] ?? ""}
                      options={options}
                      disabled={Boolean(disabled) || Boolean(field.disabled)}
                      hidden={field.hidden}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                      onChange={(v: any) =>
                        handleChange(
                          field.name,
                          Array.isArray(v) ? v.join(",") : String(v ?? "")
                        )
                      }
                      fullWidth
                      placeholder="Select..."
                      searchable={field.searchable || false}
                      onSearchInput={field.onSearchInput}
                    />
                  </Box>
                );
              }

              if (field.type === "file") {
                return (
                  <Box {...cellProps}>
                    <CustomTextField
                      label={field.label}
                      type="file"
                      inputProps={{ accept: "image/*" }}
                      onChange={(e: any) => {
                        const file = e.target.files?.[0];
                        handleChange(field.name, file);
                        setErrors((prev) => ({ ...prev, [field.name]: "" }));
                      }}
                      fullWidth
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                    />
                  </Box>
                );
              }

              if (field.type === "password") {
                return (
                  <Box {...cellProps}>
                    <CustomTextField
                      label={field.label}
                      type="password"
                      value={values[field.name] ?? ""}
                      onChange={(e: any) =>
                        handleChange(field.name, e.target.value)
                      }
                      fullWidth
                      showPasswordToggle={field.showPassword}
                    />
                  </Box>
                );
              }

              if (field.type === "switch") {
                return (
                  <Box {...cellProps}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(values[field.name])}
                          disabled={field.disabled || false}
                          onChange={(e) =>
                            handleChange(field.name, e.target.checked)
                          }
                        />
                      }
                      label={field.label}
                    />
                  </Box>
                );
              }

              if (field.type === "number") {
                // Rendered as text: input[type="number"] reports an empty
                // e.target.value for in-progress values like "0." or "-",
                // which would wipe what the user just typed.
                return (
                  <Box {...cellProps}>
                    <CustomTextField
                      label={field.label}
                      disabled={field.disabled || false}
                      highlightLabelStar={field.required || false}
                      type="text"
                      inputMode="decimal"
                      value={values[field.name] ?? ""}
                      onChange={(e: any) => {
                        const raw = String(e.target.value).replace(",", ".");
                        if (!isDecimalInput(raw)) return;
                        handleChange(field.name, raw);
                        setErrors((prev) => ({ ...prev, [field.name]: "" }));
                      }}
                      fullWidth
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                    />
                  </Box>
                );
              }

              // default text input
              return (
                <Box {...cellProps}>
                  <CustomTextField
                    label={field.label}
                    disabled={field.disabled || false}
                    highlightLabelStar={field.required || false}
                    type={field.type || "text"}
                    value={values[field.name] ?? ""}
                    onChange={(e: any) => {
                      handleChange(field.name, e.target.value);
                      setErrors((prev) => ({ ...prev, [field.name]: "" }));
                    }}
                    fullWidth
                    error={!!errors[field.name]}
                    helperText={errors[field.name]}
                  />
                </Box>
              );
            });
          })()}
        </Box>
      )}
    </CustomDialog>
  );
};

export default GenericFormDialog;
